import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../services/firebase';
import { useToast } from '../features/toast';
import Seo from '../shared/Seo';

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState('');
  const [isPasswordFieldError, setIsPasswordFieldError] = useState(false);
  const [isConfirmPasswordFieldError, setIsConfirmPasswordFieldError] = useState(false);

  const oobCode = searchParams.get('oobCode');
  const continueUrl = searchParams.get('continueUrl');

  useEffect(() => {
    const validateCode = async () => {
      if (!oobCode) {
        setIsValidating(false);
        setIsValid(false);
        setError('Invalid reset link.');
        return;
      }

      try {
        // Verify the reset code is valid
        await verifyPasswordResetCode(auth, oobCode);
        setIsValid(true);
      } catch (error) {
        console.error('Invalid password reset code:', error);
        setIsValid(false);
        if (error.code === 'auth/invalid-action-code') {
          setError('This password reset link has expired or is invalid.');
        } else if (error.code === 'auth/expired-action-code') {
          setError('This password reset link has expired. Please request a new one.');
        } else {
          setError('Invalid password reset link.');
        }
      } finally {
        setIsValidating(false);
      }
    };

    validateCode();
  }, [oobCode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setIsPasswordFieldError(false);
    setIsConfirmPasswordFieldError(false);

    if (!password || !confirmPassword) {
      setError('Please enter both password fields.');
      setIsPasswordFieldError(!password);
      setIsConfirmPasswordFieldError(!confirmPassword);
      return;
    }

    const hasRequiredLength = typeof password === 'string' && password.length >= 8;
    const hasLetter = /[A-Za-z]/.test(password || '');
    const hasNumber = /\d/.test(password || '');

    if (!hasRequiredLength || !hasLetter || !hasNumber) {
      setError('Password must be 8+ characters with letters and numbers.');
      setIsPasswordFieldError(true);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setIsConfirmPasswordFieldError(true);
      return;
    }

    if (!oobCode) {
      setError('Invalid reset link.');
      return;
    }

    setIsLoading(true);
    try {
      await confirmPasswordReset(auth, oobCode, password);
      showToast({
        message: 'Password reset successfully! You can now sign in.',
        tone: 'success',
      });
      
      // Redirect to home or continue URL after 2 seconds
      setTimeout(() => {
        navigate(continueUrl || '/', { replace: true });
      }, 2000);
    } catch (error) {
      console.error('Failed to reset password:', error);
      let errorMessage = 'Failed to reset password. Please try again.';
      if (error.code === 'auth/invalid-action-code') {
        errorMessage = 'This password reset link has expired or is invalid.';
      } else if (error.code === 'auth/expired-action-code') {
        errorMessage = 'This password reset link has expired. Please request a new one.';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak. Please choose a stronger password.';
        setIsPasswordFieldError(true);
      }
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <Seo
          title="Reset Password | PonPon!"
          canonical="https://ponpon.app/auth/reset-password"
          noindex={true}
        />
        <section className="relative w-full max-w-md">
          <div className="flex h-full flex-col items-center">
            <div className="relative flex h-full w-full min-h-[450px] flex-col rounded-[24px] border border-ponpon-teal/20 bg-white/95 p-6 shadow-lg sm:p-7">
              <div className="text-center">
                <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-ponpon-coral border-r-transparent"></div>
                <p className="text-sm font-semibold text-ponpon-navy/70">Validating reset link...</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!isValid) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4">
        <Seo
          title="Reset Password | PonPon!"
          canonical="https://ponpon.app/auth/reset-password"
          noindex={true}
        />
        <section className="relative w-full max-w-md">
          <div className="flex h-full flex-col items-center">
            <div className="relative flex h-full w-full min-h-[450px] flex-col rounded-[24px] border border-ponpon-teal/20 bg-white/95 p-6 shadow-lg sm:p-7">
              <div className="text-center">
                <div className="mb-4 text-4xl">❌</div>
                <h2 className="mb-2 font-display text-2xl font-semibold text-ponpon-navy">Invalid Link</h2>
                <p className="mb-6 text-sm font-semibold text-ponpon-navy/70">{error}</p>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="rounded-full bg-ponpon-coral px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60"
                >
                  Go Home
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-8">
      <Seo
        title="Reset Password | PonPon!"
        canonical="https://ponpon.app/auth/reset-password"
        noindex={true}
      />
      <section className="relative w-full max-w-md">
        <div className="flex h-full flex-col items-center">
          <div className="relative flex h-full w-full min-h-[450px] flex-col rounded-[24px] border border-ponpon-teal/20 bg-white/95 p-6 shadow-lg sm:p-7">
              <div className="space-y-1 text-center">
                <h2 className="font-display text-2xl font-semibold text-ponpon-navy">
                  Reset Your Password
                </h2>
              </div>

              <form onSubmit={handleSubmit} className="mt-6 flex flex-1 flex-col gap-4">
              <div className="text-left">
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setIsPasswordFieldError(false);
                    setError('');
                  }}
                  placeholder="New Password"
                  aria-label="New Password"
                  className={`w-full rounded-2xl border border-ponpon-teal/25 bg-white px-4 py-3 text-sm font-semibold text-ponpon-navy/70 shadow-sm outline-none transition placeholder:text-ponpon-navy/50 focus:border-ponpon-coral focus:text-ponpon-navy focus:ring-2 focus:ring-ponpon-coral/20 ${isPasswordFieldError ? 'border-ponpon-coral ring-2 ring-ponpon-coral/30' : ''}`}
                  disabled={isLoading}
                  required
                />
                {error && isPasswordFieldError ? (
                  <p className="mt-1 text-xs font-medium text-ponpon-coral/60">{error}</p>
                ) : (
                  <p className="mt-1 text-xs font-medium text-ponpon-navy/50">
                    Must be 8+ characters with letters and numbers
                  </p>
                )}
              </div>

              <div className="text-left">
                <input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    setIsConfirmPasswordFieldError(false);
                    setError('');
                  }}
                  placeholder="Confirm Password"
                  aria-label="Confirm Password"
                  className={`w-full rounded-2xl border border-ponpon-teal/25 bg-white px-4 py-3 text-sm font-semibold text-ponpon-navy/70 shadow-sm outline-none transition placeholder:text-ponpon-navy/50 focus:border-ponpon-coral focus:text-ponpon-navy focus:ring-2 focus:ring-ponpon-coral/20 ${isConfirmPasswordFieldError ? 'border-ponpon-coral ring-2 ring-ponpon-coral/30' : ''}`}
                  disabled={isLoading}
                  required
                />
                {error && isConfirmPasswordFieldError ? (
                  <p className="mt-1 text-xs font-medium text-ponpon-coral/60">{error}</p>
                ) : null}
              </div>

              {error && !isPasswordFieldError && !isConfirmPasswordFieldError ? (
                <div className="rounded-2xl border border-ponpon-coral/30 bg-ponpon-coral/10 p-3">
                  <p className="text-xs font-medium text-ponpon-coral/80">{error}</p>
                </div>
              ) : null}

              <div className="mt-auto flex flex-col gap-3">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex w-full items-center justify-center rounded-full bg-ponpon-coral px-6 py-2.5 text-sm font-semibold text-white transition hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isLoading ? 'Resetting Password...' : 'Reset Password'}
                </button>
                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/')}
                    className="text-xs font-semibold text-ponpon-navy/60 underline-offset-2 transition hover:text-ponpon-navy hover:underline"
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResetPasswordPage;

